/**
 * 사이트 팝업 공지 설정
 *
 * 새 공지는 ANNOUNCEMENTS에 객체를 추가하고 active를 true로 둔다.
 * id는 숨김 기록(localStorage) 키로 쓰이므로 한 번 배포한 id는 바꾸지 않는다.
 * 종료된 공지는 active: false로 두거나 지운다(내용은 git 기록에 남는다).
 *
 * 지금 보이는 공지가 여러 개면 한 팝업에 탭으로 모아 설정 순서대로 보여준다.
 *   type            : 없으면 텍스트 공지, 'cardnews'면 이미지 슬라이드(slides, socialLinks)
 *   image           : 텍스트 공지에 { src, alt }를 주면 설명·특징 대신 이미지 한 장을 보여준다.
 *                     팝업은 한눈에 알리는 곳이라 이미지를 기본으로 하고, 자세한 내용은 버튼으로 연결한다.
 *                     이미지는 backend/scripts/generate_popup_card.py로 만들고 alt에 이미지 속 글자를 그대로 적는다.
 *   tabLabel        : 탭에 보일 짧은 이름 (없으면 badge → title)
 *   startDate/endDate, includePaths, excludePathPrefixes : 게시 기간과 경로
 *   primaryButton   : { text, action: 'navigate'|'link', path|url }
 *   closeHideDays   : X·바깥 클릭·ESC·끌어 내리기 뒤 숨길 일수 (기본 1)
 *   dismissDays     : '일주일간 안보기' 뒤 숨길 일수 (기본 7)
 *   oncePerAccount  : 계정당 한 번만 (로그인 후 서버 claim)
 *   footer, dismissToast.message : 버튼 아래 안내 한 줄
 *   onImpression/onPrimaryClick/onDismiss : 추적 콜백
 *
 * 고객센터 업데이트는 이 팝업이 아니라 새 소식 팝업으로 보여 준다 (config/whatsNew.js).
 */
import { trackGroup50Event, trackPro50Event } from '../utils/eventTracker';
import {
  GROUP50_EVENT_ENABLED,
  buildGroup50LandingUrl,
} from '../utils/group50Campaign';
import {
  GROUP50_EVENT_END,
  PRO50_EVENT_ENABLED,
  PRO50_EVENT_END,
  PRO50_EVENT_START,
  buildPro50LandingUrl,
} from '../utils/pro50Campaign';

// 사이트 전역 팝업은 아직 관심을 보이지 않은 방문자에게도 뜨므로
// 기본 캠페인 UTM 귀속(ensureDefaultCampaignUtm)은 랜딩 진입 시점에 맡긴다.
const trackGroup50Popup = (action) => (
  trackGroup50Event(action, { campaign: '', entry: 'site_popup' })
);
const trackPro50Popup = (action) => (
  trackPro50Event(action, { campaign: '', entry: 'site_popup' })
);

export const ANNOUNCEMENTS = [
  {
    id: 'saenggibu-data-safety-2026-07',
    active: false,
    oncePerAccount: true,
    badge: '7월 29일 시행',
    title: '생기부 이용 기준을 안내드려요',
    description: '학생부 상업적 이용 제한 법 시행에 맞춰\n왓퀴즈의 원칙과 안전 조치를 알려드려요.',
    features: [
      '학생부를 매매하거나 영업 목적으로 제공하지 않아요',
      '입력한 자료로 AI를 학습시키거나 다른 곳에 쓰지 않아요',
      '생기부 기능은 선생님의 작성·검수 보조 도구예요',
      '연락처 같은 개인정보는 자동으로 가려서 보호해요',
    ],
    primaryButton: {
      text: '자세히 보기',
      action: 'navigate',
      path: '/help/notice/saenggibu-commercial-use-law-2026'
    },
    footer: '믿고 이용해 주셔서 감사합니다.',
    delay: 900
  },

  // 왓퀴즈 AI 앰버서더 2기 모집 카드뉴스 (2026-07-20 ~ 2026-08-21)
  {
    id: 'ambassador-season-2-2026-07',
    type: 'cardnews',
    active: false,
    title: '왓퀴즈 AI 앰버서더 2기 모집',
    tabLabel: '앰버서더 모집',
    includePaths: ['/', '/home'],
    startDate: '2026-07-20T00:00:00+09:00',
    endDate: '2026-08-21T23:59:59+09:00',
    delay: 800,
    dismissDays: 7,
    slides: [
      {
        image: '/images/announcements/ambassador-s2/slide-1.jpg',
        alt: '왓퀴즈 AI 앰버서더 2기 모집 안내. 20명을 모집하며 모집 기간은 7월 20일부터 8월 21일까지입니다.',
      },
      {
        image: '/images/announcements/ambassador-s2/slide-2.jpg',
        alt: '앰버서더 지원 대상 안내. 에듀테크, 교사 커뮤니티, 교육 콘텐츠 제작과 나눔 활동에 관심 있는 선생님을 기다립니다.',
      },
      {
        image: '/images/announcements/ambassador-s2/slide-3.jpg',
        alt: '앰버서더 활동 안내. 9월 소개 활동, 10월부터 12월까지 서비스 사용과 피드백, 다음 해 1월부터 2월까지 연수와 3기 홍보를 진행합니다.',
      },
      {
        image: '/images/announcements/ambassador-s2/slide-4.jpg',
        alt: '앰버서더 혜택 안내. 왓퀴즈 AI PRO 1년 이용권, 전용 굿즈, 수료증, 강의 지원금과 추가 홍보 혜택을 제공합니다.',
      },
    ],
    primaryButton: {
      text: '앰버서더 지원하기',
      action: 'link',
      url: 'https://forms.gle/1gCCit3Wj6dqhTHq8',
    },
    socialLinks: [
      {
        text: '인스타그램 릴스 보기',
        url: 'https://www.instagram.com/reel/DbCU-UJJyGm/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==',
      },
      {
        text: '인스타그램 게시물 보기',
        url: 'https://www.instagram.com/p/DbAY-fciag9/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==',
      },
    ],
  },

  // 10명 이상 단체구매 50% — 9월 2학기 한정 (/events/group-50, 캠페인 group50_2026q3)
  // 9/30 23:59:59 KST에 자동 종료되고, 10월부터는 아래 PRO 최대 50% 팝업이 뜬다.
  {
    id: 'group50-second-semester-2026-09',
    active: GROUP50_EVENT_ENABLED,
    endDate: GROUP50_EVENT_END,
    // 이벤트 랜딩·허브에서는 다른 캠페인 흐름을 방해하지 않도록 팝업을 숨긴다.
    // /chat 은 "뭘 하러 왔는지" 고르는 첫 화면이라 진입 직후 전면 팝업을 띄우지 않는다
    // (토스 다크패턴 방지 1원칙). 사이드바 '요금제 단체 50%' 배지와 /pricing 진입은 그대로 남는다.
    excludePathPrefixes: ['/events', '/chat'],
    badge: '9월 2학기 한정',
    title: '10명 이상이면 반값이에요',
    tabLabel: '9월 단체 50%',
    image: {
      src: '/images/announcements/group50-2026-09/card.jpg',
      alt: '10명 이상이면 반값이에요. 9월 30일까지 단체구매하면 6개월 PRO가 1인 55,000원이에요.',
    },
    primaryButton: {
      text: '할인가 확인하기',
      action: 'navigate',
      // from=site_popup — 랜딩 landing_viewed가 사이드바·요금제 등 다른 진입점과 구분해 집계한다.
      path: buildGroup50LandingUrl('site_popup'),
    },
    // X·딤·스와이프는 3일, 일주일간 안보기는 7일. 계정 단위 영구 숨김이 아니다.
    closeHideDays: 3,
    dismissDays: 7,
    // CTA가 아닌 닫기 뒤에만 띄운다 — 팝업을 닫아도 사이드바 "요금제"에서 다시 찾을 수 있다는 안내.
    dismissToast: {
      message: '이벤트는 요금제 메뉴에서 언제든 다시 볼 수 있어요',
      type: 'info',
      duration: 6000,
    },
    delay: 1000,
    onImpression: () => trackGroup50Popup('popup_viewed'),
    onPrimaryClick: () => trackGroup50Popup('popup_cta_clicked'),
    onDismiss: () => trackGroup50Popup('popup_dismissed'),
  },

  // 10월 PRO 최대 50% — 인원 제한 없음 (/events/pro-50, 캠페인 pro50_2026q4)
  // 새 id라 9월 팝업을 닫은 사용자에게도 한 번 더 뜬다. 기간이 겹치는 공지는 한 팝업에 탭으로 함께 뜬다.
  {
    id: 'pro50-october-2026-10',
    active: PRO50_EVENT_ENABLED,
    startDate: PRO50_EVENT_START,
    endDate: PRO50_EVENT_END,
    // 요금제·결제 화면은 이미 이벤트가를 보여준다. 결제 중에 랜딩으로 되돌리는 팝업을 띄우지 않는다.
    excludePathPrefixes: ['/events', '/chat', '/pricing', '/subscription', '/bulk-purchase', '/payment', '/billing'],
    badge: '10월 한정',
    title: 'PRO 1년, 반값이에요',
    tabLabel: '10월 PRO 반값',
    image: {
      src: '/images/announcements/pro50-2026-10/card.jpg',
      alt: 'PRO 1년, 반값이에요. 10월 31일까지 1명부터 12개월 200,000원이 100,000원이에요.',
    },
    primaryButton: {
      text: '할인가 확인하기',
      action: 'navigate',
      path: buildPro50LandingUrl('site_popup'),
    },
    closeHideDays: 3,
    dismissDays: 7,
    dismissToast: {
      message: '이벤트는 요금제 메뉴에서 언제든 다시 볼 수 있어요',
      type: 'info',
      duration: 6000,
    },
    delay: 1000,
    onImpression: () => trackPro50Popup('popup_viewed'),
    onPrimaryClick: () => trackPro50Popup('popup_cta_clicked'),
    onDismiss: () => trackPro50Popup('popup_dismissed'),
  },

  // 10월 12일(월) 19:00~20:00 온라인 연수 참가자 모집 (시작 시각에 내린다)
  {
    id: 'training-2026-10-12',
    type: 'cardnews',
    active: false,
    startDate: '2026-10-01T00:00:00+09:00',
    endDate: '2026-10-12T19:00:00+09:00',
    excludePathPrefixes: ['/events', '/chat', '/pricing', '/subscription', '/bulk-purchase', '/payment', '/billing'],
    badge: '10월 12일(월) 19시',
    title: '왓퀴즈 AI 연수 참가자 모집',
    tabLabel: '10/12 연수',
    slides: [
      {
        image: '/images/announcements/training-2026-10/card.jpg',
        alt: '왓퀴즈 AI 연수 참가자 모집. 10월 12일 월요일 저녁 7시에 줌에서 만나요.',
      },
    ],
    primaryButton: {
      text: '연수 신청하기',
      action: 'link',
      url: 'https://forms.gle/JSdgmc6iMbS41jQLA',
    },
    footer: '10월 12일(월) 19:00~20:00 · 줌 온라인',
    closeHideDays: 3,
    dismissDays: 7,
    delay: 1000,
  },

  // 한국교원대 융합교육연구소 x 왓퀴즈AI 10/31(토) 고1 교과 실증 연구 — 영어 마감, 국어·수학 모집 (10/23까지)
  // 10/16 조건 완화 공지(아래)로 바꿨다.
  {
    id: 'ceri-pilot-recruit-2026-10',
    type: 'cardnews',
    active: false,
    startDate: '2026-10-14T00:00:00+09:00',
    endDate: '2026-10-23T23:59:59+09:00',
    excludePathPrefixes: ['/events', '/chat', '/pricing', '/subscription', '/bulk-purchase', '/payment', '/billing'],
    badge: '10월 31일(토) 교원대',
    title: '고1 국어·수학 선생님 모집',
    tabLabel: '교원대 실증',
    slides: [
      {
        image: '/images/announcements/ceri-pilot-2026-10/card.jpg',
        alt: '고1 국어·수학 선생님 모집. 10월 31일(토) 한국교원대. 연구 참여 시 연구비 30만 원. 영어는 마감됐어요.',
      },
    ],
    primaryButton: {
      text: '참여 신청하기',
      action: 'link',
      url: 'https://forms.gle/ZAgab3j77ZZwS81i9',
    },
    footer: '10월 31일(토) 14:00~16:00 · 노트북 지참',
    closeHideDays: 3,
    dismissDays: 7,
    delay: 1000,
  },

  // 같은 실증 연구 추가 모집 — 고등학교 국어·수학·사회·과학·영어 선생님, 학년 상관없이 7명 (10/16~10/23)
  // 10/22 모집 마감으로 내렸다. 마감 안내는 고객센터 공지(ceri-pilot-recruit-2026-10)에 남긴다.
  {
    id: 'ceri-pilot-recruit-open-2026-10',
    type: 'cardnews',
    active: false,
    startDate: '2026-10-16T00:00:00+09:00',
    endDate: '2026-10-23T23:59:59+09:00',
    excludePathPrefixes: ['/events', '/chat', '/pricing', '/subscription', '/bulk-purchase', '/payment', '/billing'],
    badge: '10월 31일(토) 교원대',
    title: '고등학교 선생님 추가 모집',
    tabLabel: '교원대 실증',
    slides: [
      {
        image: '/images/announcements/ceri-pilot-open-2026-10/card.jpg',
        alt: '추가 모집. 10월 31일 토요일 14:00–16:00. 한국교원대 융합교육연구소 × 왓퀴즈AI 고등학교 선생님 모집. 학년 상관없어요. 국어·수학·사회·과학·영어 교과. 남은 자리 7명. 10월 31일(토) 한국교원대, 개인 노트북 지참. 교원연구비 30만 원.',
      },
    ],
    primaryButton: {
      text: '참여 신청하기',
      action: 'link',
      url: 'https://forms.gle/ZAgab3j77ZZwS81i9',
    },
    footer: '10월 31일(토) 14:00~16:00 · 노트북 지참',
    closeHideDays: 3,
    dismissDays: 7,
    delay: 1000,
  },

  // 10월 28일(수) 19:00~20:00 온라인 연수 참가자 모집 — 할인 다음 탭 (시작 시각에 내린다)
  {
    id: 'training-2026-10-28',
    type: 'cardnews',
    active: true,
    startDate: '2026-10-16T00:00:00+09:00',
    endDate: '2026-10-28T19:00:00+09:00',
    excludePathPrefixes: ['/events', '/chat', '/pricing', '/subscription', '/bulk-purchase', '/payment', '/billing'],
    badge: '10월 28일(수) 19시',
    title: '왓퀴즈 AI 연수 참가자 모집',
    tabLabel: '10/28 연수',
    slides: [
      {
        image: '/images/announcements/training-2026-10-28/card.jpg',
        alt: '왓퀴즈 AI 연수 참가자 모집. 10월 28일 수요일 저녁 7시에 줌에서 만나요.',
      },
    ],
    primaryButton: {
      text: '연수 신청하기',
      action: 'link',
      url: 'https://forms.gle/JSdgmc6iMbS41jQLA',
    },
    footer: '10월 28일(수) 19:00~20:00 · 줌 온라인',
    closeHideDays: 3,
    dismissDays: 7,
    delay: 1000,
  },

  // 한국교원대 Anchor 사업단 x 왓퀴즈AI 활용 실증(에듀테크 실증 4기) 참여교사 모집 — 11/9(월) 15:00 마감에 내린다
  {
    id: 'knue-pilot-recruit-2026-11',
    type: 'cardnews',
    active: true,
    startDate: '2026-11-04T00:00:00+09:00',
    endDate: '2026-11-09T15:00:00+09:00',
    excludePathPrefixes: [
      '/events', '/chat', '/pricing', '/subscription', '/bulk-purchase', '/payment', '/billing',
      '/help/notice/knue-pilot-recruit-2026-11',
    ],
    badge: '11월 9일(월) 15시 마감',
    title: '왓퀴즈 AI 활용 실증 참여교사 모집',
    tabLabel: '교원대 실증',
    slides: [
      {
        image: '/images/announcements/knue-pilot-2026-11/card.jpg',
        alt: '한국교원대 × 왓퀴즈AI. AI 활용 실증 참여교사 모집. 교사 활동비 30만 원. 11월 9일(월) 15시까지 신청.',
      },
    ],
    primaryButton: {
      text: '참여 신청하기',
      action: 'link',
      url: 'https://forms.gle/StpcFJkZwt6u82y78',
    },
    socialLinks: [
      { text: '신청 양식·소개서 받기', url: 'https://drive.google.com/drive/folders/1IpAiX9vnibiTCEOP1HA_zVdPAIMohOKK?usp=sharing' },
      { text: '자세히 보기', url: '/help/notice/knue-pilot-recruit-2026-11' },
    ],
    footer: '초·중·고 선생님 최대 10명 · 사전 회의 11월 10일(화) 17시 줌',
    closeHideDays: 3,
    dismissDays: 7,
    delay: 1000,
  },
];

const isWithinAnnouncementPeriod = (announcement, now) => {
  const timestamp = now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (!Number.isFinite(timestamp)) return false;

  const startTimestamp = announcement.startDate
    ? new Date(announcement.startDate).getTime()
    : Number.NEGATIVE_INFINITY;
  const endTimestamp = announcement.endDate
    ? new Date(announcement.endDate).getTime()
    : Number.POSITIVE_INFINITY;

  return timestamp >= startTimestamp && timestamp <= endTimestamp;
};

const matchesAnnouncementPath = (announcement, pathname) => {
  if (!announcement.includePaths?.length) return true;
  return announcement.includePaths.includes(pathname);
};

const matchesAnnouncementExcludePaths = (announcement, pathname) => {
  if (!announcement.excludePathPrefixes?.length || !pathname) return true;
  return !announcement.excludePathPrefixes.some(prefix => (
    pathname === prefix || pathname.startsWith(`${prefix}/`)
  ));
};

// 이 경로(와 하위 경로)에서는 어떤 팝업 공지도 띄우지 않는다.
// 로그인·광고 랜딩·이벤트 랜딩처럼 전환 흐름이 있는 화면과 운영·기관 전용 화면이다.
// /ad/pro 는 /pricing 으로 리다이렉트되지만, 깜빡임 방지를 위해 prefix로 막는다.
export const ANNOUNCEMENT_HIDDEN_PATH_PREFIXES = [
  '/auth',
  '/login',
  '/register',
  '/reset-password',
  '/lp',
  '/ad',
  '/private/daycare-evaluation',
  '/private/watkids-eval',
  '/pilot/rise',
  '/events/exhibition',
  '/events/ambassador',
  '/events/2nd-semester-ai',
  '/events/year-end-festa',
  '/events/group-50',
  '/events/pro-50',
  '/events/pro-ai',
  '/events/scoring',
  '/events/hwp',
  '/events/hwp-exam',
  '/events/hwp-notice',
  '/events/hwp-record',
  '/school',
  '/partner',
  // 공유받은 대화: 처음 온 선생님이 대화·파일을 먼저 보게 한다.
  '/share',
  '/training',
  '/admin',
  '/bulk-purchase',
  '/help/notice/saenggibu-commercial-use-law-2026',
];

export const isAnnouncementHiddenPath = (pathname = '') => (
  ANNOUNCEMENT_HIDDEN_PATH_PREFIXES.some((prefix) => (
    pathname === prefix || pathname.startsWith(`${prefix}/`)
  ))
);

/**
 * 지금 경로·시각에 띄울 수 있는 공지(텍스트·카드뉴스)를 설정 순서대로 돌려준다.
 * 브라우저 숨김 기록과 계정 1회 claim은 팝업이 따로 확인한다.
 * @param {string} pathname 현재 경로
 * @param {Date|string|number} now 테스트 가능한 현재 시각
 * @returns {Object[]}
 */
export const getVisibleAnnouncements = (pathname = '', now = new Date()) => {
  if (isAnnouncementHiddenPath(pathname)) return [];
  return ANNOUNCEMENTS.filter(announcement => (
    announcement.active
    && isWithinAnnouncementPeriod(announcement, now)
    && matchesAnnouncementPath(announcement, pathname)
    && matchesAnnouncementExcludePaths(announcement, pathname)
  ));
};
